<template>
    <div class="customer-group-order-form-page-1">
        <div class="form-group row">
            <label class="col-md-2 col-form-label" for="customer">{{ _.capitalize(customerLabel) }}</label>
            <div class="col-md-5">
                <v-select
                    v-dusk="'customer-select'"
                    id="customer"
                    v-model="lSelectedCustomer"
                    :options="customers"
                    :input-id="'customerSearch'"
                    label="name"
                    :disabled="!allowEditCustomer"
                >
                    <div slot="no-options">Aucun {{ customerLabel }} ne correspond à votre recherche</div>
                </v-select>
            </div>
        </div>

        <div v-if="lSelectedCustomer" class="form-group row">
            <div class="col-md-4 offset-md-2">
                <b>Jours de livraison :</b> {{ lSelectedCustomer.delivery_days.join(', ') }}<br>
                <b>Jour de fin de préparation :</b> {{ lSelectedCustomer.end_preparation_day }}<br>
                <b>Jour de livraison :</b> J<br>
                <b>Jour de distribution :</b> {{ lSelectedCustomer.meta.subcustomers_delivery_day }}<br>
            </div>
        </div>

        <div v-if="lSelectedCustomer" class="form-group row">
            <label class="col-md-2 col-form-label" for="deliveryDate">Date de livraison</label>
            <DeliveryDatepicker
                ref="deliveryDatepicker"
                id="deliveryDate"
                class="delivery-date col-md-10"
                :customer="customerForDatePickers"
                :order="order"
                :can-select-red-dates="canSelectRedDates"
                v-model="lDeliveryDate"
            />
        </div>

        <template v-if="lDeliveryDate">
            <div class="form-group row">
                <label class="col-md-2 col-form-label" for="endPreparationDate">Date de fin de préparation</label>
                <ForcePreparationDatepicker
                    ref="endPreparationDatepicker"
                    id="endPreparationDate"
                    class="col-md-10"
                    :customer="customerForDatePickers"
                    :delivery-date="lDeliveryDate"
                    :can-select-red-dates="canSelectRedDates"
                    v-model="lEndPreparationDate"
                />
            </div>
            <div class="form-group row">
                <div class="offset-md-2">
                    <CheckExistingOrder
                        v-if="lEndPreparationDate"
                        :customer="lSelectedCustomer"
                        :end-preparation-date="formattedLEndPreparationDateYmd"
                        :order="order"
                        :view-existing-order-href="viewExistingOrderHrefCallback"
                        :edit-existing-order-href="editExistingOrderHrefCallback"
                        @existing-order-result="cbExistingOrderResult"
                    />
                </div>
            </div>
            <div class="form-group row">
                <AlertsZone
                    v-if="warningZoneDisplayed"
                    v-dusk="'alerts-zone-warning'"
                    class="offset-md-2"
                    type="warning"
                    title="Attention : validation requise"
                    :messages="warningMessages"
                />
            </div>
            <div class="form-group row">
                <AlertsZone
                    v-if="dangerZoneDisplayed"
                    v-dusk="'alerts-zone-danger'"
                    class="offset-md-2"
                    type="danger"
                    title="Attention : validation requise"
                    :messages="dangerMessages"
                />
            </div>
        </template>

        <div v-if="lEndPreparationDate" class="form-group row">
            <label class="col-md-2 col-form-label" for="closingDate">Date de fermeture des commandes</label>
            <ClosingDatepicker
                ref="closingDatepicker"
                id="closingDate"
                class="closing-date col-md-10"
                :end-preparation-date="lEndPreparationDate"
                v-model="lClosingDate"
            />
        </div>

        <div v-if="lClosingDate" class="form-group row">
            <label class="col-md-2 col-form-label" for="image">Image</label>
            <input
                class="col-md-10"
                type="file"
                id="image"
                accept="image/jpeg, image/png"
                @change="onImageChange"
            />
            <div class="offset-md-2 col-md-10">
                <img
                    v-if="lImageSrc"
                    class="mt-1"
                    width="150"
                    :src="lImageSrc"
                />
                <img
                    v-else-if="order && order.logo_url"
                    width="150"
                    :src="order.logo_url"
                />
            </div>
        </div>

        <div v-if="lClosingDate" class="form-group row">
            <label class="col-md-2 col-form-label" for="description">Description</label>
            <textarea id="description" cols="60" rows="3" v-model="lDescription"></textarea>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-12">
                <a v-if="backUrl" class="btn btn-outline-dark float-left" :href="backUrl">
                    <i class="fas fa-chevron-left"></i> Quitter la prise de commande
                </a>

                <template v-if="lEndPreparationDate">
                    <template v-if="needAuthorizationToken && !authorizationToken">
                        <AuthorizationModal
                            :token-type="authorizationTokenType"
                            :token-payload="authorizationTokenPayload"
                            @authorised="onAuthorizationModalAuthorize"
                        >
                            <template #button="sp">
                                <AuthorizationModalButton
                                    :class="dangerZoneDisplayed ? 'btn-danger' : 'btn-warning'"
                                    :disabled="!canClickNext"
                                    @click="sp.showModal()"
                                >
                                    Suivant
                                </AuthorizationModalButton>
                            </template>
                            <template #messages>
                                <AlertsZone
                                    v-if="warningZoneDisplayed"
                                    type="warning"
                                    title="Attention : validation requise"
                                    :messages="warningMessages"
                                />
                                <AlertsZone
                                    v-if="dangerZoneDisplayed"
                                    type="danger"
                                    title="Attention : validation requise"
                                    :messages="dangerMessages"
                                />
                            </template>
                        </AuthorizationModal>
                    </template>
                    <button v-else type="button" class="btn btn-success float-right" @click="onClickNext" :disabled="!canClickNextGreen">
                        <i class="fas fa-chevron-right"></i> Suivant
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faAddressCard, faLockAlt} from '@fortawesome/pro-duotone-svg-icons';
import {faCrown} from '@fortawesome/pro-solid-svg-icons';

import {momentDay, momentDaysAreSame} from "../../moment-with-helpers";
import {readFileAsDataURL} from "../../helpers";
import {
    dangerZoneDisplayed,
    orderDatesDangerMessages,
    orderDatesWarningMessages,
    warningZoneDisplayed
} from "../compositions/OrderWarnings";

import vSelect from 'vue-select';
import AlertsZone from "../AlertsZone";
import AuthorizationModal from "../AuthorizationModal";
import ClosingDatepicker from "../ClosingDatepicker";
import DeliveryDatepicker from "../DeliveryDatepicker";
import ForcePreparationDatepicker from "../ForcePreparationDatepicker";
import AuthorizationModalButton from "./../AuthorizationModalButton";

export default {
    components: {
        AuthorizationModalButton,
        AlertsZone,
        'v-select': vSelect,
        AuthorizationModal,
        ClosingDatepicker,
        DeliveryDatepicker,
        ForcePreparationDatepicker,
    },

    props: {
        order: {
            type: Object,
            required: false,
        },
        customers: {
            type: Array,
            required: true,
        },
        customerLabel: {
            type: String,
            required: true,
        },
        selectedCustomer: {
            type: Object,
            required: false,
        },
        deliveryDate: {
            type: Object,
            required: false,
        },
        endPreparationDate: {
            type: Object,
            required: false,
        },
        closingDate: {
            type: Date,
            required: false,
        },
        image: {
            type: File,
            required: false,
            default: null,
        },
        description: {
            type: String,
            required: false,
        },
        allowEditCustomer: {
            type: Boolean,
            required: false,
            default: true,
        },
        canSelectRedDates: {
            type: Boolean,
            required: true,
        },
        authorizationTokenType: {
            type: String,
            required: true,
        },
        backUrl: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            authorizationToken: null,
            existingOrderResult: null,

            lSelectedCustomer: this.selectedCustomer,
            lDeliveryDate: this.deliveryDate,
            lEndPreparationDate: this.endPreparationDate,
            lClosingDate: this.closingDate,
            lImage: this.image,
            lImageSrc: null,
            lDescription: this.description || '',
        };
    },

    created() {
        library.add(faAddressCard, faCrown, faLockAlt);
    },

    mounted() {
        this.loadImagePreview();
    },

    methods: {
        async loadImagePreview() {
            if (this.lImage instanceof File) {
                this.lImageSrc = await readFileAsDataURL(this.lImage);
            } else {
                this.lImageSrc = null;
            }
        },

        async onImageChange(e) {
            console.log(e.target.files[0]);
            if (e.target.files && e.target.files[0]) {
                this.lImage = e.target.files[0];
            } else {
                this.lImage = null;
            }
        },

        onAuthorizationModalAuthorize(token) {
            this.authorizationToken = token;

            this.onClickNext();
        },

        onClickNext() {
            this.$emit('submit', {
                selectedCustomer: this.lSelectedCustomer,
                deliveryDate: this.lDeliveryDate,
                endPreparationDate: this.lEndPreparationDate,
                closingDate: this.lClosingDate,
                image: this.lImage,
                description: this.lDescription,
                authorizationToken: this.authorizationToken,
            });
        },

        viewExistingOrderHrefCallback(order) {
            return '/espace-employes/commandes/preparation/commande/' + order.id;
        },

        editExistingOrderHrefCallback(order) {
            if (this.lSelectedCustomer) {
                return '/espace-employes/commandes/commande-groupement-clients/' + order.id + '/edition'
            }
        },

        cbExistingOrderResult(result) {
            this.existingOrderResult = result;
        },
    },

    watch: {
        lImage() {
            this.loadImagePreview();
        },

        lSelectedCustomer(newValue, oldValue) {
            this.authorizationToken = null;
            this.lDeliveryDate = null;
            this.lEndPreparationDate = null;
            this.lClosingDate = null;
            this.existingOrderResult = null;
        },

        lDeliveryDate(newDate, oldDate) {
            if (!oldDate || !newDate || !momentDaysAreSame(oldDate.date, newDate.date)) {
                this.authorizationToken = null;
                this.lEndPreparationDate = null;
                this.lClosingDate = null;
                this.existingOrderResult = null;
            }
        },

        lEndPreparationDate(newDate, oldDate) {
            if (!oldDate || !newDate || !momentDaysAreSame(oldDate.date, newDate.date)) {
                this.authorizationToken = null;
                this.lClosingDate = null;
                this.existingOrderResult = null;
            }
        },

        lClosingDate(newDate, oldDate) {
            if (!oldDate || !newDate || !momentDaysAreSame(oldDate.date, newDate.date)) {
                this.authorizationToken = null;
            }
        },
    },

    computed: {
        customerForDatePickers() {
            return {type: this.lSelectedCustomer.type, id: this.lSelectedCustomer.id};
        },

        hasNoExistingOrder() {
            return this.existingOrderResult != null && this.existingOrderResult.order === null;
        },

        canClickNext() {
            return !!this.lSelectedCustomer
                && !!this.lDeliveryDate
                && !!this.lEndPreparationDate
                && !!this.lClosingDate
                && this.hasNoExistingOrder
                && (!this.lImage || (this.lImage && this.lImageSrc));
        },

        canClickNextGreen() {
            return this.canClickNext && !this.needAuthorizationToken;
        },

        warningMessages() {
            return orderDatesWarningMessages(this.lDeliveryDate)
        },

        dangerMessages() {
            return orderDatesDangerMessages(this.lDeliveryDate, this.lEndPreparationDate)
        },

        warningZoneDisplayed,

        dangerZoneDisplayed,

        needAuthorizationToken() {
            return !this.authorizationToken && (this.dangerZoneDisplayed || this.warningZoneDisplayed);
        },

        authorizationTokenPayload() {
            return {
                order_id: (this.order && this.order.id) || null,
                token_type: this.authorizationTokenType,
                customer_type: this.lSelectedCustomer.type,
                customer_id: this.lSelectedCustomer.id,
                delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                end_preparation_date: momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD'),
                closing_date: momentDay(this.lClosingDate).format('YYYY-MM-DD'),
                description: this.lDescription,
            };
        },

        formattedLEndPreparationDateYmd() {
            return momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD');
        },

        formattedDeliveryDate() {
            if (this.lDeliveryDate) {
                return momentDay(this.lDeliveryDate.date).format('dddd Do MMMM YYYY');
            }
        },
    }
}
</script>

<style lang="scss">
.customer-group-order-form-page-1 {
    .btn-social > :first-child {
        height: inherit;
    }
}
</style>
